import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { tournamentsService, leaderboardService, activityService } from '../../services/supabaseService';
import { matchesService } from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import Header from '../../components/navigation/Header';
import TournamentCard from './components/TournamentCard';
import LeaderboardPanel from './components/LeaderboardPanel';
import UpcomingMatchCard from './components/UpcomingMatchCard';

import QuickStatsWidget from './components/QuickStatsWidget';
import RecentActivityFeed from './components/RecentActivityFeed';

const UserDashboard = () => {
  const { userProfile } = useAuth();
  const [activeTournaments, setActiveTournaments] = useState([]);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTournamentId, setActiveTournamentId] = useState(null);
  const [totalPredictions, setTotalPredictions] = useState(0);
  const [upcomingMatches, setUpcomingMatches] = useState([]);

  useEffect(() => {
    if (userProfile) {
      fetchDashboardData();
    }
  }, [userProfile]);

  // Real-time leaderboard subscription for active tournament
  useEffect(() => {
    if (!activeTournamentId) return;

    const subscription = leaderboardService?.subscribeToLeaderboard(
      activeTournamentId,
      (updatedLeaderboard) => {
        // Transform updated leaderboard data
        const transformedLeaderboard = updatedLeaderboard?.map(entry => ({
          userId: entry?.userId,
          rank: entry?.rank,
          name: entry?.userProfiles?.fullName || entry?.userProfiles?.email,
          avatar: entry?.userProfiles?.avatarUrl || 'https://via.placeholder.com/150',
          avatarAlt: `${entry?.userProfiles?.fullName || 'User'} avatar`,
          points: entry?.totalPoints,
          predictions: entry?.totalPredictions,
          change: 0
        })) || [];

        setLeaderboardData(transformedLeaderboard);
      }
    );

    // Cleanup subscription on unmount
    return () => {
      subscription?.unsubscribe();
    };
  }, [activeTournamentId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch tournaments
      const tournaments = await tournamentsService?.getAll();

      // Transform tournaments
      const transformedTournaments = tournaments?.map(tournament => ({
        id: tournament?.id,
        name: tournament?.name,
        format: tournament?.format,
        status: tournament?.status,
        startDate: tournament?.startDate,
        endDate: tournament?.endDate,
        location: tournament?.location,
        participants: tournament?.tournamentParticipants?.length || 0,
        isParticipating: tournament?.isParticipating || false,
        prize1: tournament?.prize1,
        prize2: tournament?.prize2,
        prize3: tournament?.prize3,
        image: tournament?.bannerImage || 'https://images.unsplash.com/photo-1730739628981-6537b299aea3',
        imageAlt: tournament?.bannerImageAlt || 'Cricket tournament banner'
      })) || [];

      // Fetch predictions counts for all tournaments
      const predictionCounts = await Promise.all(
        (transformedTournaments || [])?.map(async (tournament) => {
          const { count: tournamentPredCount } = await supabase
            ?.from('tournament_predictions')
            ?.select('*', { count: 'exact', head: true })
            ?.eq('tournament_id', tournament?.id);

          const { count: matchPredCount } = await supabase
            ?.from('match_predictions')
            ?.select('match_id, matches!inner(tournament_id)', { count: 'exact', head: true })
            ?.eq('matches.tournament_id', tournament?.id);

          return { id: tournament?.id, count: (tournamentPredCount || 0) + (matchPredCount || 0) };
        })
      );
      const predictionCountMap = Object.fromEntries(predictionCounts?.map(p => [p?.id, p?.count]));

      const tournamentsWithPredictions = transformedTournaments?.map(t => ({
        ...t,
        predictions: predictionCountMap?.[t?.id] || 0
      }));

      setActiveTournaments(tournamentsWithPredictions);

      // Get leaderboard for first active tournament
      const activeTournament = transformedTournaments?.find(t => t?.status === 'active');
      if (activeTournament) {
        setActiveTournamentId(activeTournament?.id);
        const leaderboard = await leaderboardService?.getByTournament(activeTournament?.id);
        const transformedLeaderboard = leaderboard?.map(entry => ({
          userId: entry?.userId,
          rank: entry?.rank,
          name: entry?.userProfiles?.fullName || entry?.userProfiles?.email,
          avatar: entry?.userProfiles?.avatarUrl || 'https://via.placeholder.com/150',
          avatarAlt: `${entry?.userProfiles?.fullName || 'User'} avatar`,
          points: entry?.totalPoints,
          predictions: entry?.totalPredictions,
          change: 0
        })) || [];
        setLeaderboardData(transformedLeaderboard);

        // Fetch actual total predictions count for current user
        if (userProfile?.id) {
          const userTotalPreds = await leaderboardService?.getUserTotalPredictions(
            userProfile?.id,
            activeTournament?.id
          );
          setTotalPredictions(userTotalPreds);
        }
      }

      // Transform activities
      const activities = await activityService?.getUserActivity(5);
      const transformedActivities = activities?.map(activity => ({
        id: activity?.id,
        type: activity?.activityType,
        title: activity?.title,
        description: activity?.description,
        time: activity?.timestamp,
        icon: activity?.icon || 'Activity'
      })) || [];

      setRecentActivities(transformedActivities);

      // Fetch upcoming matches for user
      if (userProfile?.id) {
        const matches = await matchesService?.getUpcomingForUser(userProfile?.id, 5);
        const transformedMatches = matches?.map(match => ({
          id: match?.id,
          tournament: match?.tournament?.name,
          matchType: match?.matchType || 'T20',
          team1: match?.team1?.name,
          team1Logo: match?.team1?.logo || '/assets/images/no_image.png',
          team1LogoAlt: `${match?.team1?.name} logo`,
          team2: match?.team2?.name,
          team2Logo: match?.team2?.logo || '/assets/images/no_image.png',
          team2LogoAlt: `${match?.team2?.name} logo`,
          matchTime: match?.matchDate,
          venue: match?.venue,
          predictionLockTime: match?.matchLockTime || match?.matchDate,
          hasPredicted: match?.hasPredicted || false
        })) || [];
        setUpcomingMatches(transformedMatches);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const quickStats = [
    { label: 'Active Tournaments', value: activeTournaments?.filter(t => t?.status === 'active')?.length || 0, icon: 'Trophy', color: 'primary' },
    { label: 'Total Predictions', value: totalPredictions, icon: 'Target', color: 'success' },
    { label: 'Current Rank', value: leaderboardData?.find(l => l?.userId === userProfile?.id)?.rank || '-', icon: 'Award', color: 'warning' },
    { label: 'Total Points', value: leaderboardData?.find(l => l?.userId === userProfile?.id)?.points || 0, icon: 'TrendingUp', color: 'error' }
  ];

  if (loading) {
    return (
      <>
        <Helmet>
          <title>User Dashboard - PowerPlay</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>User Dashboard - PowerPlay</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <button
                onClick={fetchDashboardData}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>User Dashboard - PowerPlay</title>
        <meta name="description" content="View active cricket tournaments, track your predictions, and monitor leaderboard standings on PowerPlay dashboard" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="main-content">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-5 md:mb-6">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Welcome back, {userProfile?.fullName}!
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Track your predictions and compete with cricket fans worldwide
              </p>
            </div>

            <div className="mb-5 md:mb-6">
              <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                Upcoming Matches
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {upcomingMatches?.length > 0 ? (
                  upcomingMatches?.map((match) => (
                    <UpcomingMatchCard key={match?.id} match={match} />
                  ))
                ) : (
                  <div className="bg-card rounded-lg shadow-elevation-sm p-6 text-center col-span-full">
                    <p className="text-muted-foreground">No upcoming matches in your tournaments</p>
                  </div>
                )}
              </div>
            </div>

            <div className="mb-5 md:mb-6">
              <QuickStatsWidget stats={quickStats} />
            </div>

            <div className="space-y-5 md:space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground">
                    Active Tournaments
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeTournaments?.map((tournament) =>
                  <TournamentCard key={tournament?.id} tournament={tournament} />
                  )}
                </div>
              </div>

              <LeaderboardPanel 
                leaderboardData={leaderboardData} 
                currentUser={userProfile} 
                tournamentId={activeTournamentId}
                tournamentName={activeTournaments?.find(t => t?.id === activeTournamentId)?.name || ''}
              />

              <RecentActivityFeed activities={recentActivities} />
            </div>
          </div>
        </main>
      </div>
    </>);

};

export default UserDashboard;